// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FeaturedProducts from "@adl/App/Components/Header/FeaturedProducts";
import loadCategoryFeaturedProducts from "@adl/App/Handlers/Categories/LoadCategoryFeaturedProducts";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { mergeWithCss } from "@adl/XDL/Utilities";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { ResolveThunks } from "react-redux";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    categoryId = "categoryId",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.categoryId]: string;
    };
    extendedStyles: FeaturedProductsWidgetStyles;
}

const mapDispatchToProps = {
    loadCategoryFeaturedProducts,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedProductsWidgetStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    title?: TypographyProps;
}

export const featuredProductsWidgetStyles: FeaturedProductsWidgetStyles = {
    title: {
        variant: "h4",
        color: "text",
        css: css`
            margin-bottom: 20px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 215px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const FeaturedProductsWidget: FC<Props> = ({ id, fields, extendedStyles, loadCategoryFeaturedProducts }) => {
    const { categoryId, title } = fields;
    const [allProductIds, setAllProductIds] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [styles] = React.useState(() => mergeWithCss(featuredProductsWidgetStyles, extendedStyles));

    useEffect(() => {
        if (categoryId && categoryId !== emptyGuid && !isLoading) {
            setIsLoading(true);
            loadCategoryFeaturedProducts({
                widgetId: id,
                categoryId,
                onError: () => {
                    setIsLoading(false);
                },
                onSuccess: result => {
                    setAllProductIds(result);
                    setIsLoading(false);
                },
            });
        }
        return () => {
            setIsLoading(false);
        };
    }, [categoryId]);
    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="ShopByProduct">
                {fields.title && <Typography {...styles.title}>{title}</Typography>}
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="shopByCategory_Loading">
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    if (allProductIds.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="ShopByProduct">
            {fields.title && <Typography {...styles.title}>{title}</Typography>}
            <FeaturedProducts featuredProductsIds={allProductIds} widgetId={id} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(FeaturedProductsWidget),
    definition: {
        displayName: "Featured Products",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Featured Products",
                fieldType: "General",
            },
            {
                name: fields.categoryId,
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                singleCategorySelection: () => true,
            },
        ],
    },
};

export default widgetModule;
